// Habit definitions with personalization rules
export const habitDefinitions = {
  // Daily habits - appear every day
  daily: [
    {
      id: 'morning-cleanse',
      name: 'Morning Cleanse',
      description: 'Gentle cleanser to start your day',
      icon: '🧼',
      category: 'cleansing',
      frequency: 'daily',
      skinTypes: ['all'],
      ageRanges: ['all'],
      timeOfDay: 'morning'
    },
    {
      id: 'moisturize-am',
      name: 'Morning Moisturizer',
      description: 'Hydrate and protect your skin',
      icon: '💧',
      category: 'moisturizing',
      frequency: 'daily',
      skinTypes: ['all'],
      ageRanges: ['all'],
      timeOfDay: 'morning'
    },
    {
      id: 'sunscreen',
      name: 'Apply Sunscreen',
      description: 'SPF 30+ broad spectrum protection',
      icon: '☀️',
      category: 'protection',
      frequency: 'daily',
      skinTypes: ['all'],
      ageRanges: ['all'],
      timeOfDay: 'morning',
      priority: 'high'
    },
    {
      id: 'evening-cleanse',
      name: 'Evening Cleanse',
      description: 'Remove makeup and daily buildup',
      icon: '🌙',
      category: 'cleansing',
      frequency: 'daily',
      skinTypes: ['all'],
      ageRanges: ['all'],
      timeOfDay: 'evening'
    },
    {
      id: 'moisturize-pm',
      name: 'Night Moisturizer',
      description: 'Repair and restore overnight',
      icon: '🌜',
      category: 'moisturizing',
      frequency: 'daily',
      skinTypes: ['all'],
      ageRanges: ['all'],
      timeOfDay: 'evening'
    },
    {
      id: 'drink-water',
      name: 'Drink Water',
      description: 'Stay hydrated for healthy skin',
      icon: '💦',
      category: 'wellness',
      frequency: 'daily',
      skinTypes: ['all'],
      ageRanges: ['all'],
      timeOfDay: 'all'
    }
  ],

  // Weekly habits - appear 1-2x per week
  weekly: [
    {
      id: 'exfoliate-gentle',
      name: 'Gentle Exfoliation',
      description: 'Remove dead skin cells',
      icon: '✨',
      category: 'exfoliation',
      frequency: 'weekly',
      timesPerWeek: 1,
      skinTypes: ['dry', 'sensitive', 'normal'],
      ageRanges: ['all'],
      timeOfDay: 'evening'
    },
    {
      id: 'exfoliate-bha',
      name: 'BHA Exfoliation',
      description: 'Deep clean pores with salicylic acid',
      icon: '🧪',
      category: 'exfoliation',
      frequency: 'weekly',
      timesPerWeek: 2,
      skinTypes: ['oily', 'combination'],
      ageRanges: ['all'],
      timeOfDay: 'evening'
    },
    {
      id: 'hydrating-mask',
      name: 'Hydrating Mask',
      description: 'Deep moisture treatment',
      icon: '🥒',
      category: 'treatment',
      frequency: 'weekly',
      timesPerWeek: 1,
      skinTypes: ['dry', 'normal'],
      ageRanges: ['all'],
      timeOfDay: 'evening'
    },
    {
      id: 'clay-mask',
      name: 'Clay Mask',
      description: 'Oil control and pore cleansing',
      icon: '🏺',
      category: 'treatment',
      frequency: 'weekly',
      timesPerWeek: 1,
      skinTypes: ['oily', 'combination'],
      ageRanges: ['all'],
      timeOfDay: 'evening'
    },
    {
      id: 'face-oil',
      name: 'Nourishing Face Oil',
      description: 'Extra hydration and glow',
      icon: '🌿',
      category: 'treatment',
      frequency: 'weekly',
      timesPerWeek: 2,
      skinTypes: ['dry'],
      ageRanges: ['30s', '40s', '50plus'],
      timeOfDay: 'evening'
    }
  ],

  // Monthly habits - appear once per month
  monthly: [
    {
      id: 'retinol-treatment',
      name: 'Retinol Treatment',
      description: 'Anti-aging and skin renewal',
      icon: '🧬',
      category: 'anti-aging',
      frequency: 'monthly',
      skinTypes: ['all'],
      ageRanges: ['30s', '40s', '50plus'],
      timeOfDay: 'evening',
      priority: 'high'
    },
    {
      id: 'collagen-mask',
      name: 'Collagen Boosting Mask',
      description: 'Firmness and elasticity treatment',
      icon: '💎',
      category: 'anti-aging',
      frequency: 'monthly',
      skinTypes: ['all'],
      ageRanges: ['40s', '50plus'],
      timeOfDay: 'evening'
    },
    {
      id: 'professional-treatment',
      name: 'Professional Treatment',
      description: 'Consider a facial or dermatologist visit',
      icon: '🏥',
      category: 'professional',
      frequency: 'monthly',
      skinTypes: ['all'],
      ageRanges: ['all'],
      timeOfDay: 'all'
    }
  ]
};

// Helper function to get personalized habits for a user
export const getPersonalizedHabits = (userProfile) => {
  const { skinType, ageRange, concerns } = userProfile;
  const personalizedHabits = [];

  // Process all frequency categories
  Object.keys(habitDefinitions).forEach(frequency => {
    habitDefinitions[frequency].forEach(habit => {
      // Check if habit applies to user's skin type
      const skinTypeMatch = habit.skinTypes.includes('all') || habit.skinTypes.includes(skinType);

      // Check if habit applies to user's age range
      const ageRangeMatch = habit.ageRanges.includes('all') || habit.ageRanges.includes(ageRange);

      if (skinTypeMatch && ageRangeMatch) {
        personalizedHabits.push({
          ...habit,
          userSpecific: true
        });
      }
    });
  });

  // Add concern-specific habits if concerns are defined
  if (concerns && concerns.length > 0) {
    concerns.forEach(concern => {
      const concernHabits = generateConcernSpecificHabits(concern, skinType, ageRange);
      personalizedHabits.push(...concernHabits);
    });
  }

  return personalizedHabits;
};

// Generate habits specific to user concerns
const generateConcernSpecificHabits = (concern, skinType, ageRange) => {
  const concernHabits = [];

  const concernHabitMap = {
    'dryness': [
      {
        id: `hydrating-serum-${concern.priority}`,
        name: 'Hydrating Serum',
        description: 'Apply hyaluronic acid serum for deep hydration',
        icon: '💧',
        category: 'treatment',
        frequency: 'daily',
        skinTypes: ['all'],
        ageRanges: ['all'],
        timeOfDay: 'morning',
        priority: concern.priority === 1 ? 'high' : 'normal',
        concernBased: true,
        concern: concern.concern
      }
    ],
    'acne': [
      {
        id: `acne-treatment-${concern.priority}`,
        name: 'Acne Treatment',
        description: 'Apply salicylic acid treatment to problem areas',
        icon: '🎯',
        category: 'treatment',
        frequency: 'daily',
        skinTypes: ['all'],
        ageRanges: ['all'],
        timeOfDay: 'evening',
        priority: concern.priority === 1 ? 'high' : 'normal',
        concernBased: true,
        concern: concern.concern
      }
    ],
    'aging': [
      {
        id: `anti-aging-serum-${concern.priority}`,
        name: 'Anti-Aging Serum',
        description: 'Apply retinol or peptide serum for anti-aging benefits',
        icon: '⏰',
        category: 'treatment',
        frequency: 'daily',
        skinTypes: ['all'],
        ageRanges: ['30s', '40s+'],
        timeOfDay: 'evening',
        priority: concern.priority === 1 ? 'high' : 'normal',
        concernBased: true,
        concern: concern.concern
      }
    ],
    'dark-spots': [
      {
        id: `brightening-serum-${concern.priority}`,
        name: 'Brightening Serum',
        description: 'Apply vitamin C serum to fade dark spots',
        icon: '🌟',
        category: 'treatment',
        frequency: 'daily',
        skinTypes: ['all'],
        ageRanges: ['all'],
        timeOfDay: 'morning',
        priority: concern.priority === 1 ? 'high' : 'normal',
        concernBased: true,
        concern: concern.concern
      }
    ]
  };

  const habits = concernHabitMap[concern.concern] || [];
  return habits.filter(habit => {
    const skinTypeMatch = habit.skinTypes.includes('all') || habit.skinTypes.includes(skinType);
    const ageRangeMatch = habit.ageRanges.includes('all') || habit.ageRanges.includes(ageRange);
    return skinTypeMatch && ageRangeMatch;
  });
};
